import { ICollection, IEntity } from 'firebase-firestorm/lib'
import { v4 as uuid } from 'uuid'

export abstract class BaseRepository<T extends IEntity> {
  protected abstract getCollection(): ICollection<T>;

  protected abstract getNewInstance(): T;

  protected refs: string[] = [ 'ref' ]

  protected _objects: Map<string, { entity: T, timestamp: number }> = new Map()
  protected cacheDuration: number = 60000 // stale after 1 minute

  protected transactionCounter: number = 0
  protected pendingWriteIds: Set<string> = new Set()

  public async findAllOrNew(...ids: string[]): Promise<T[]> {
    return await Promise.all(ids.map(async (id: string) => await this.findOrNew(id)))
  }

  public async findAll(...ids: string[]): Promise<T[]> {
    return ( await Promise.all(ids.map(async (id: string) => await this.find(id))) ).filter(Boolean) as T[]
  }

  public async find(id: string): Promise<T | null> {
    const cacheValue = this.getCacheValue(id)
    if (cacheValue) {
      return cacheValue

    }
    else {
      const record = await this.getCollection().get(id)
      record && this.setCacheValue(record)

      return record
    }
  }

  public async findOrNew(id: string): Promise<T> {
    if (!id) {
      const newObject = this.getNewInstance()
      this.setCacheValue(newObject)

      return newObject
    }

    let object = await this.find(id)
    if (!object) {
      object = this.getNewInstance()
      object.id = id

      this.setCacheValue(object)
    }

    return object
  }

  public async save(object: T): Promise<T> {
    if (this.transactionCounter > 0) {
      this.pendingWriteIds.add(object.id)
      return this.setCacheValue(object)
    }

    await this.writeToFirestore(object)
    object.id = object.ref.id
    return this.setCacheValue(object)
  }

  public async clone(object: T): Promise<T> {
    const clone = await this.getCollection().get(object.id)
    if (!clone) {
      throw Error('Unable to clone: Object not found. Object Id: ' + object.id)
    }

    clone.id = uuid()
    delete clone.ref

    return this.setCacheValue(clone)
  }

  protected async writeToFirestore(object: T) {
    const newObject = await this.getCollection().create(object)
    console.log('saved to firestore', object.id)
    if (newObject) {
      for (const ref of this.refs) {
        // @ts-ignore
        object[ref] = newObject[ref]
      }
    }
  }

  public startTransaction() {
    this.transactionCounter++
  }

  public async commit() {
    this.transactionCounter--

    if (this.transactionCounter === 0) {
      console.log('writing', this.pendingWriteIds)

      if (process.env.NODE_ENV !== 'test') {
        await Promise.all([ ...this.pendingWriteIds.values() ]
            .map(async (id: string) => {
              const value = this.getCacheValue(id)
              if (value) {
                await this.writeToFirestore(value)
              }
            }))
      }
      else {
        for (const id of this.pendingWriteIds) {
          const value = this.getCacheValue(id)
          if (value) {
            await this.writeToFirestore(value)
          }
        }
      }

      this.pendingWriteIds.clear()
    }
  }

  protected cacheQueryResult(result: T[]) {
    result.forEach((entity: T) => this.setCacheValue(entity))
    return result
  }

  protected setCacheValue(entity: T) {
    // console.log('cache set', entity.id)
    this._objects.set(entity.id, { entity, timestamp: ( new Date ).getTime() })

    return entity
  }

  protected getCacheValue(id: string) {
    const lookup = this._objects.get(id)
    if (lookup && lookup.timestamp + this.cacheDuration > ( new Date ).getTime()) {
      // console.log('cache hit', id)
      return lookup.entity
    }
    else {
      // console.log('cache miss', id)
      return null
    }
  }
}
